/**
 * Caché — what every reload computes before it publishes (2026-10-26).
 *
 * Decisions D6 = C1a + C2a (maqueta `docs/mockups/cache-builder-2026-10-26.html`):
 * its own page under Administración, and a switch per report row with
 * the cost in the same row, saved on click, no confirmation dialog.
 *
 *   - Tenant settings: the languages to warm (only reports whose SQL
 *     reads `{filter:__locale}` care) and the alert threshold.
 *   - The day's estimate, recalculated on every click from what the last
 *     reload of each project measured (`estimate.ts`).
 *   - Per project, the reports that have button filters, each with its
 *     switch «Todas las combinaciones», and the state of the project's
 *     last pre-publish warm (icon + word + colour, never colour alone).
 *
 * Changes apply from each project's next reload; the portal's 60 s cycle
 * fills a newly cacheable view before that.
 */

import { Button, Checkbox, Input } from "@omnitok/ui";
import {
  Callout,
  DataTable,
  EmptyState,
  PageContainer,
  PageHeader,
  Section,
  SectionStack,
  StatusChip,
  type Column,
} from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { resolveLocalized, type DisplayLabel } from "@pervasivemind/dashboard-render";
import { AlertCircle, Check, Database, Globe, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useT } from "@/i18n";
import { parseProjectName } from "@/lib/project-name";

import {
  cacheApi,
  type CacheOverview,
  type CacheProject,
  type CacheReport,
} from "./cache-api";
import {
  effectiveEnabled,
  extraSeconds,
  formatDuration,
  projectTotals,
  sumTotals,
} from "./estimate";

const ALERT_MINUTES_MAX = 24 * 60;

function hasButtons(report: CacheReport): boolean {
  return report.buttons.length > 0;
}

export function CachePage(): JSX.Element {
  const { t, locale } = useT();
  const [data, setData] = useState<CacheOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  // The option per report as saved on the server, updated on each click
  // (the overview is not refetched for one switch).
  const [options, setOptions] = useState<Record<string, boolean>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  // Settings being edited (saved with the button).
  const [locales, setLocales] = useState<string[]>([]);
  const [alertMinutes, setAlertMinutes] = useState<string>("");
  const [savingSettings, setSavingSettings] = useState(false);

  const showFeedback = useCallback((msg: string): void => {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 3000);
  }, []);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const overview = await cacheApi.overview();
      setData(overview);
      setOptions({});
      setLocales(overview.settings.locales);
      setAlertMinutes(String(overview.settings.alert_minutes));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const projectName = useCallback(
    (p: CacheProject): string =>
      resolveLocalized(parseProjectName(p.name) as DisplayLabel, locale) || p.slug,
    [locale],
  );

  const workers = data?.settings.workers ?? 1;
  const languages = locales.length;

  const totals = useMemo(
    () =>
      data
        ? sumTotals(data.projects.map((p) => projectTotals(p, options, languages)))
        : null,
    [data, options, languages],
  );

  const settingsDirty =
    data !== null &&
    (alertMinutes !== String(data.settings.alert_minutes) ||
      locales.join(",") !== data.settings.locales.join(","));

  const toggleLocale = (loc: string, on: boolean): void => {
    if (!data) return;
    const next = on ? [...locales, loc] : locales.filter((l) => l !== loc);
    // Supported order, the order the server stores.
    setLocales(data.settings.supported_locales.filter((l) => next.includes(l)));
  };

  const saveSettings = async (): Promise<void> => {
    const minutes = Number(alertMinutes);
    if (locales.length === 0) {
      setError(t("cache.settings.needOneLocale"));
      return;
    }
    if (!Number.isInteger(minutes) || minutes < 1 || minutes > ALERT_MINUTES_MAX) {
      setError(t("cache.settings.badMinutes", { max: ALERT_MINUTES_MAX }));
      return;
    }
    setSavingSettings(true);
    try {
      const saved = await cacheApi.saveSettings({ locales, alert_minutes: minutes });
      setData((prev) =>
        prev
          ? {
              ...prev,
              settings: {
                ...prev.settings,
                locales: saved.locales,
                alert_minutes: saved.alert_minutes,
              },
            }
          : prev,
      );
      setError(null);
      showFeedback(t("cache.settings.saved"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
    } finally {
      setSavingSettings(false);
    }
  };

  const setOption = async (
    project: CacheProject,
    report: CacheReport,
    enabled: boolean,
  ): Promise<void> => {
    setBusyKey(report.key);
    try {
      await cacheApi.setReportOption(report.key, enabled);
      setOptions((prev) => ({ ...prev, [report.key]: enabled }));
      setError(null);
      showFeedback(t("cache.option.saved", { project: projectName(project) }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
    } finally {
      setBusyKey(null);
    }
  };

  const buttonsText = (report: CacheReport): string => {
    const parts = report.buttons.map(
      (b) => `${resolveLocalized(b.label as DisplayLabel, locale) || b.key} ${b.options}`,
    );
    return `${parts.join(" × ")} = ${report.variants_if_enabled}`;
  };

  const lastWarmChip = (p: CacheProject): JSX.Element => {
    const w = p.last_warm;
    if (w === null) {
      return <StatusChip label={t("cache.last.none")} tone="neutral" />;
    }
    const when = w.finished_at
      ? new Date(w.finished_at).toLocaleString(locale, {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";
    if (w.status === "failed") {
      return (
        <StatusChip
          label={t("cache.last.failed", { when })}
          tone="danger"
          title={w.error_summary ?? undefined}
        />
      );
    }
    const dur = w.seconds !== null ? formatDuration(w.seconds, locale) : "";
    if (w.over_threshold) {
      return (
        <StatusChip label={t("cache.last.overThreshold", { when, dur })} tone="warning" />
      );
    }
    return <StatusChip label={t("cache.last.complete", { when, dur })} tone="success" />;
  };

  const columns = (project: CacheProject): Column<CacheReport>[] => [
    {
      id: "report",
      header: t("cache.col.report"),
      cell: (r) => (
        <div className="min-w-0">
          <div className="font-semibold text-neutral-900">
            {r.is_group_options
              ? t("cache.groupOptions", { group: r.group_key ?? "" })
              : resolveLocalized(r.name, locale) || r.key}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-2xs text-neutral-500">{r.key}</span>
            {r.reads_locale && (
              <StatusChip
                label={t("cache.readsLocale")}
                tone="info"
                icon={<Globe size={12} strokeWidth={2.5} />}
              />
            )}
          </div>
        </div>
      ),
    },
    {
      id: "buttons",
      header: t("cache.col.buttons"),
      cell: (r) => <span className="text-xs text-neutral-700">{buttonsText(r)}</span>,
    },
    {
      id: "option",
      header: t("cache.col.option"),
      cell: (r) => {
        const on = effectiveEnabled(r, project, options);
        if (r.is_group_options) {
          return (
            <span className="text-xs text-neutral-600">
              {t(on ? "cache.option.followsOn" : "cache.option.followsOff")}
            </span>
          );
        }
        return (
          <Checkbox
            toggle
            checked={on}
            disabled={busyKey === r.key}
            label={t(on ? "cache.option.yes" : "cache.option.no")}
            aria-label={t("cache.option.aria", {
              report: resolveLocalized(r.name, locale) || r.key,
            })}
            onCheckedChange={(next: boolean) => void setOption(project, r, next)}
          />
        );
      },
    },
    {
      id: "cost",
      header: t("cache.col.cost"),
      align: "right",
      cell: (r) => {
        const extra = extraSeconds(r, languages);
        if (extra === null) {
          return <span className="text-xs text-neutral-500">{t("cache.noData")}</span>;
        }
        const on = effectiveEnabled(r, project, options);
        const value = extra / workers;
        const text = value < 1 ? "< 1 s" : `+${formatDuration(value, locale)}`;
        return on ? (
          <span className="text-xs font-semibold text-neutral-900">{text}</span>
        ) : (
          <span className="text-xs text-neutral-500">
            {text}
            <span className="block text-2xs">{t("cache.ifEnabled")}</span>
          </span>
        );
      },
    },
  ];

  const withButtons = data?.projects.filter((p) => p.reports.some(hasButtons)) ?? [];
  const without = data?.projects.filter((p) => !p.reports.some(hasButtons) && p.reports.length > 0) ?? [];
  const withoutTotals = sumTotals(
    without.map((p) => projectTotals(p, options, languages)),
  );
  const broken = data?.projects.flatMap((p) =>
    Object.entries(p.broken).map(([key, err]) => ({ project: projectName(p), key, err })),
  ) ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t("cache.title")}
        subtitle={t("cache.subtitle")}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void load()}
            leftIcon={<RefreshCw size={14} />}
          >
            {t("common.refresh")}
          </Button>
        }
      />

      {feedback && (
        <div
          role="status"
          className="mb-4 flex items-center gap-2 rounded-lg border border-success/40 bg-success/10 px-4 py-2 text-sm text-success-dark"
        >
          <Check size={14} /> {feedback}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="mb-4 flex items-center gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark"
        >
          <AlertCircle size={14} /> {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-auto text-error-dark"
            aria-label={t("common.cancel")}
          >
            ✕
          </button>
        </div>
      )}

      {loading && !data && (
        <div className="flex justify-center py-16">
          <Spinner size={20} ariaLabel={t("common.loading")} />
        </div>
      )}

      {data && totals && (
        <SectionStack>
          <div className="grid gap-5 lg:grid-cols-3">
            <Section title={t("cache.day.title")}>
              <p className="font-display text-[26px] font-extrabold leading-tight text-neutral-900">
                {formatDuration(totals.seconds / workers, locale)}
              </p>
              <p className="mt-1 text-xs text-neutral-600">
                {t("cache.day.breakdown", {
                  defaults: formatDuration(totals.defaultsSeconds / workers, locale),
                  extra: formatDuration(
                    (totals.seconds - totals.defaultsSeconds) / workers,
                    locale,
                  ),
                  languages,
                })}
              </p>
              <p className="mt-1 text-2xs text-neutral-500">
                {t("cache.day.note", { workers })}
              </p>
              {totals.unmeasured > 0 && (
                <p className="mt-1 text-2xs text-warning-dark">
                  {t("cache.day.unmeasured", { count: totals.unmeasured })}
                </p>
              )}
            </Section>

            <Section title={t("cache.locales.title")}>
              <div className="space-y-2">
                {data.settings.supported_locales.map((loc) => (
                  <Checkbox
                    key={loc}
                    label={t(`cache.locale.${loc}`)}
                    checked={locales.includes(loc)}
                    disabled={savingSettings}
                    onCheckedChange={(on: boolean) => toggleLocale(loc, on)}
                  />
                ))}
              </div>
              <p className="mt-2 text-2xs leading-relaxed text-neutral-500">
                {t("cache.locales.help")}
              </p>
            </Section>

            <Section title={t("cache.alert.title")}>
              <label className="block text-xs text-neutral-700">
                {t("cache.alert.label")}
                <Input
                  type="number"
                  min={1}
                  max={ALERT_MINUTES_MAX}
                  value={alertMinutes}
                  disabled={savingSettings}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAlertMinutes(e.target.value)
                  }
                  className="mt-1 w-28"
                />
              </label>
              <p className="mt-2 text-2xs leading-relaxed text-neutral-500">
                {t("cache.alert.help")}
              </p>
              <div className="mt-3">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!settingsDirty || savingSettings}
                  onClick={() => void saveSettings()}
                >
                  {t("cache.settings.save")}
                </Button>
              </div>
            </Section>
          </div>

          {broken.length > 0 && (
            <Callout tone="danger" title={t("cache.broken.title")}>
              <ul className="list-disc pl-4">
                {broken.map((b) => (
                  <li key={`${b.project}:${b.key}`}>
                    <span className="font-semibold">{b.project}</span> ·{" "}
                    <span className="font-mono">{b.key}</span>: {b.err}
                  </li>
                ))}
              </ul>
            </Callout>
          )}

          {withButtons.length === 0 && (
            <EmptyState
              icon={<Database size={22} />}
              title={t("cache.empty.title")}
              description={t("cache.empty.desc")}
            />
          )}

          {withButtons.map((p) => {
            const tot = projectTotals(p, options, languages);
            const rows = p.reports.filter(hasButtons);
            return (
              <Section
                key={p.slug}
                title={projectName(p)}
                description={t("cache.project.desc", {
                  count: rows.length,
                  dur: formatDuration(tot.seconds / workers, locale),
                })}
                actions={lastWarmChip(p)}
                flush
              >
                <DataTable rowKey={(r) => r.key} columns={columns(p)} rows={rows} />
              </Section>
            );
          })}

          {without.length > 0 && (
            <Section
              title={t("cache.without.title", { count: without.length })}
              description={t("cache.without.desc", {
                dur: formatDuration(withoutTotals.seconds / workers, locale),
              })}
            >
              <div className="flex flex-wrap gap-2">
                {without.map((p) => (
                  <span key={p.slug} className="inline-flex items-center gap-2 text-xs text-neutral-700">
                    {projectName(p)} {lastWarmChip(p)}
                  </span>
                ))}
              </div>
            </Section>
          )}
        </SectionStack>
      )}
    </PageContainer>
  );
}
